/**
 * CheckoutFlow — a self-contained "buy Premium" button + every status-code
 * branch that can follow it. Owns its OWN fetch/loading/error state (mirrors
 * ExamScoreReport's TailoredPracticeCta / guardian's NotifyToggle
 * self-containment pattern), so any surface can drop in
 * `<CheckoutFlow beneficiaryId={...} />` and get the full, identical
 * status-code contract — no duplicated branching between the upgrade
 * dialog, the guardian entry, and the Premium hub page.
 *
 * PHASES:
 *   unconfigured   — no purchasable price wired yet (billingConfig
 *                    defaultPriceId() is ''); a calm "not available yet"
 *                    note, no button at all (never a doomed 422).
 *   idle           — the "Upgrade to Premium" button.
 *   submitting     — POST in flight; the SAME button, busy (see FOCUS), +
 *                    PendingStatus.
 *   redirecting    — the POST succeeded; navigating to Stripe (best-effort
 *                    brief state before the browser unloads).
 *   already        — 409 already_entitled: nothing left to buy — celebrate,
 *                    refresh the shared entitlement context, offer "Manage
 *                    billing".
 *   guardianReq    — 403 checkout_guardian_required: warm "ask a parent/
 *                    guardian" or "verify your link" panel (server message
 *                    already distinguishes which) — NO retry (nothing
 *                    changes by clicking again).
 *   inProgress     — 409 checkout_in_progress: server message already says
 *                    whether it's a genuine collision or a settling payment
 *                    — a manual, non-looping "Try again" is safe either way.
 *   ageRequired    — 426, reached ONLY after the global age/consent
 *                    interceptor's DOB-capture modal was DISMISSED (a
 *                    submitted DOB transparently retries the SAME request
 *                    and this phase is never reached) — mirrors
 *                    TailoredPracticeCta's `dobRequired` fallback exactly.
 *   stripeDown     — 502/503: a genuine SYSTEM fault (not the student's
 *                    doing) — the one billing state that legitimately uses
 *                    the error/red palette. Safe to retry: the purchase
 *                    intent is reused under the same idempotency key.
 *   invalid        — 400: a misconfigured price/student id — not
 *                    user-actionable, dismiss only.
 *   error          — anything else (network, unexpected 5xx).
 *
 * Every terminal phase's "dismiss" calls `onDismiss` if the caller supplied
 * one (e.g. UpgradeDialog closes itself), else resets to `idle` in place
 * (PremiumHub / GuardianPremiumEntry are persistent page sections, not
 * modals — resetting lets the student try again without navigating away).
 *
 * ELIGIBILITY-GATED SELF-PURCHASE CTAs (F14-PR3.2, review S1/HIGH + S2/HIGH),
 * driven by `useBilling().selfCheckout` (the entitlement payload's
 * `self_checkout`: 'allowed' | 'guardian_required' | 'age_unknown' |
 * 'not_student', or `null` on an older backend that doesn't send it yet —
 * see BillingContext.js). ONLY applies to a SELF-purchase (no
 * `beneficiaryId` prop) — a guardian buying FOR a linked child
 * (GuardianPremiumEntry) is a completely different eligibility question the
 * server already enforces via `checkout_guardian_required`, and this field
 * says nothing about it:
 *   'guardian_required' — a MINOR's account. Renders the guardian-guidance
 *                  panel DIRECTLY, in place of the idle button, BEFORE the
 *                  phase state machine is even consulted — so there is no
 *                  render in which a buy button could exist, and no
 *                  `POST /billing/checkout-session` is EVER sent from this
 *                  session. (The identical panel is still reachable
 *                  REACTIVELY, from a real 403, when this field is absent —
 *                  that path is untouched, and is exactly the safe fallback
 *                  for an older backend.)
 *   'not_student'  — a non-student account (e.g. a bare teacher/admin) has
 *                  no self entitlement to buy (Premium is a per-STUDENT
 *                  grant). Renders a "buy it for your students instead"
 *                  panel pointing at the guardian purchase entry, same
 *                  "before the state machine, no POST" treatment as above.
 *   'age_unknown'  — the account has no DOB on file yet, so the backend
 *                  cannot yet decide 'allowed' vs. 'guardian_required'. The
 *                  idle button stays, but its click handler (`beginCheckout`)
 *                  proactively pops the SAME global DOB-capture modal the
 *                  reactive 426 path already uses (`ageConsentController.
 *                  requestDob()`) BEFORE ever attempting the POST, instead
 *                  of firing a doomed request and letting it 426 first —
 *                  strictly better UX (no flash of "Starting checkout…"
 *                  before the modal appears) and data minimization (no
 *                  purchase-intent request sent from an account whose age
 *                  is genuinely unknown). Falls through to `start()`
 *                  directly once resolved, or on any OTHER `selfCheckout`
 *                  value (including `null`/absent) — the existing REACTIVE
 *                  426 handling below (`ageRequired` phase) is unchanged
 *                  and still the fallback for an older backend.
 *
 * BFCACHE RECOVERY (review S5): see the `pageshow` effect below — a
 * `submitting`/`redirecting` phase frozen by a back/forward-cache restore
 * resets to an actionable idle/unconfigured state instead of staying stuck
 * on a permanently busy button.
 *
 * FOCUS: a click here changes what is on screen, and the control the student
 * used can go with it — a result panel replaces the Upgrade button, "Try
 * again" and a local dismiss swap a panel back for it. Focus used to fall to
 * <body> each time; inside the upgrade dialog (its date-of-birth prompt
 * dismissed, a checkout refused) a keyboard or screen-reader student was left
 * nowhere, with the panel that had just appeared unread. So:
 *   - The control that starts a request keeps focus while it runs. The Upgrade
 *     button is ONE element through idle → submitting → redirecting, and it and
 *     "Manage billing" turn busy with aria-disabled — never `disabled` or
 *     `loading`, which a browser drops focus from. <Button> refuses an
 *     aria-disabled button's activation.
 *   - Each click arms hooks/useFocusHandoff, which has the rule. Every view a
 *     click can lead to spreads `landing` on the element it hands focus to: a
 *     result panel's message line (tabIndex=-1, never #main-content), the
 *     portal refusal's note, or the Upgrade button a retry or a local dismiss
 *     brings back. The message is the landing in every panel, the role="alert"
 *     one included: a focus move can cut an alert's speech short, and a line,
 *     unlike a button, cannot be pressed by an Enter still held from the click.
 *   - Nothing else arms it. The entitlement arriving moves focus only while a
 *     click's hand-off is still pending, and a bfcache restore drops the
 *     hand-off.
 *   - One click can change nothing on screen: "Try again" on the
 *     date-of-birth panel, when the prompt it raises is dismissed again. The
 *     prompt returns focus to that button, its opener, so the button refuses a
 *     held Enter itself.
 *
 * Props:
 *   beneficiaryId  optional — a linked student's id (guardian-routed
 *                  purchase). Omit to buy for the caller.
 *   priceId        optional — defaults to billingConfig.defaultPriceId().
 *   buttonLabel    optional — default 'Upgrade to Premium'.
 *   onDismiss      optional — () => void, see above.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import axios from 'axios';
import { Button, PendingStatus, Spinner } from '../components/ui';
import useFocusHandoff from '../hooks/useFocusHandoff';
import { useBilling } from './BillingContext';
import { defaultPriceId } from './billingConfig';
import {
  createCheckoutSession,
  isAlreadyEntitled,
  isCheckoutGuardianRequired,
  isCheckoutInProgress,
  friendlyError,
  nextSteps,
} from './billingApi';
import { usePortalOpen } from './usePortalOpen';
import { isAgeRequired } from '../coppa/coppaApi';
import { requestDob } from '../coppa/ageConsentController';
import refuseHeldEnter from '../utils/refuseHeldEnter';
import './CheckoutFlow.css';

// sessionStorage key ActivationPage.js reads back after Stripe's STATIC
// success-path redirect (billing_checkout_success_path has no {intent_id}
// segment — see that file's header for why this hand-off exists at all).
export const PENDING_INTENT_KEY = 'liang_billing_pending_intent_id';

function NextStepsList({ steps }) {
  if (!steps.length) return null;
  return (
    <ul className="checkout-flow__steps">
      {steps.map((step, i) => (
        <li key={i}>{step}</li>
      ))}
    </ul>
  );
}

export default function CheckoutFlow({
  beneficiaryId = null,
  priceId,
  buttonLabel = 'Upgrade to Premium',
  onDismiss,
}) {
  const billing = useBilling();
  // Hook, not a re-derived local copy — see usePortalOpen.js's header for
  // the drift this fixes (F14-PR3.1, review B1/HIGH). Called unconditionally
  // here (Rules of Hooks) even though its result only renders in the
  // 'already' phase below.
  const portal = usePortalOpen();
  // Destructured to a plain local identifier so `dismiss` below can list the
  // exact stable function it depends on (usePortalOpen's own useCallback([])
  // guarantees `reset` never changes identity) — `portal` itself is a fresh
  // object literal every render, and react-hooks/exhaustive-deps has no way
  // to know only ONE of its fields is read inside that callback, so listing
  // `portal.reset` as a MemberExpression dependency doesn't satisfy it.
  const { reset: resetPortal, openPortal } = portal;
  const resolvedPriceId = priceId || defaultPriceId();
  const [phase, setPhase] = useState(resolvedPriceId ? 'idle' : 'unconfigured');
  const [message, setMessage] = useState('');
  const [steps, setSteps] = useState([]);
  const unmountedRef = useRef(false);

  // Self-purchase eligibility (review S1/S2) — see the file header. `null`
  // (backend field absent, or a guardian-routed purchase, where this
  // question doesn't apply at all) falls through to every branch below
  // unaffected — the exact "safe fallback" the review requires.
  const selfCheckout = beneficiaryId ? null : billing.selfCheckout;

  // What is on screen, for the focus hand-off (header, FOCUS): the phase, but
  // the eligibility panels replace the whole state machine, and the
  // already-entitled panel runs a request of its own.
  let view = phase;
  if (selfCheckout === 'guardian_required' || selfCheckout === 'not_student') {
    view = selfCheckout;
  } else if (phase === 'already') {
    view = `already:${portal.state}`;
  }
  const inFlight =
    view === 'submitting' || view === 'redirecting' || view === 'already:loading';
  const {
    landing,
    arm: armFocusHandoff,
    disarm: disarmFocusHandoff,
  } = useFocusHandoff(view, inFlight);

  // The four handlers below are the clicks that arm the hand-off. Each is an
  // onClick, so `event.currentTarget` is the button the student used; `start`
  // also runs from beginCheckout with no event.
  const dismiss = useCallback((event) => {
    if (onDismiss) {
      onDismiss();
      return;
    }
    // The panel's buttons leave with it: hand focus to the Upgrade button.
    armFocusHandoff(event?.currentTarget);
    setPhase(resolvedPriceId ? 'idle' : 'unconfigured');
    setMessage('');
    setSteps([]);
    resetPortal();
  }, [onDismiss, resolvedPriceId, resetPortal, armFocusHandoff]);

  const manageBilling = useCallback((event) => {
    armFocusHandoff(event?.currentTarget);
    openPortal();
  }, [armFocusHandoff, openPortal]);

  const start = useCallback(async (event) => {
    armFocusHandoff(event?.currentTarget);
    setPhase('submitting');
    setMessage('');
    setSteps([]);
    try {
      const { checkout_url: checkoutUrl, intent_id: intentId } =
        await createCheckoutSession({
          priceId: resolvedPriceId,
          beneficiaryId,
        });
      if (unmountedRef.current) return;
      try {
        sessionStorage.setItem(PENDING_INTENT_KEY, intentId);
      } catch {
        // Storage unavailable (private mode / quota) — ActivationPage.js
        // degrades to its own "no active purchase to check" empty state.
        // The purchase itself is unaffected either way.
      }
      setPhase('redirecting');
      window.location.href = checkoutUrl;
    } catch (err) {
      if (unmountedRef.current) return;
      if (axios.isCancel?.(err) || err?.name === 'CanceledError') return;

      if (isAlreadyEntitled(err)) {
        setPhase('already');
        setMessage(friendlyError(err, 'This student already has Premium.'));
        setSteps(nextSteps(err));
        billing.refresh();
        return;
      }
      if (isCheckoutGuardianRequired(err)) {
        setPhase('guardianReq');
        setMessage(
          friendlyError(err, 'A parent or guardian needs to set this up.'),
        );
        setSteps(nextSteps(err));
        return;
      }
      if (isCheckoutInProgress(err)) {
        setPhase('inProgress');
        setMessage(
          friendlyError(err, 'A purchase for this student is already in progress.'),
        );
        setSteps(nextSteps(err));
        return;
      }
      if (isAgeRequired(err)) {
        setPhase('ageRequired');
        setMessage('Add your date of birth first, then try again.');
        return;
      }
      const status = err?.response?.status;
      if (status === 502 || status === 503) {
        setPhase('stripeDown');
        setMessage(
          friendlyError(err, "We couldn't reach the payment provider."),
        );
        return;
      }
      if (status === 400) {
        setPhase('invalid');
        setMessage(friendlyError(err, "That plan isn't available right now."));
        setSteps(nextSteps(err));
        return;
      }
      setPhase('error');
      setMessage(friendlyError(err, 'Something went wrong. Please try again.'));
    }
  }, [resolvedPriceId, beneficiaryId, billing, armFocusHandoff]);

  // Unmount guard, mirrors ExamEntryCard/TailoredPracticeCta.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // Bfcache recovery (review S5) — a `submitting`/`redirecting` phase is a
  // REAL outbound request/navigation in flight; if the browser restores this
  // exact page from the back/forward cache after the student hits BACK (from
  // Stripe, or before the redirect even completed), the button would stay
  // frozen forever with no way to ever click it again. `pageshow`'s
  // `persisted` flag is the one reliable signal a bfcache restore (vs. a
  // fresh load) happened; only the two phases that can actually get stuck
  // this way are reset, and only back to whatever the button should show
  // right now (idle vs. unconfigured) — every OTHER phase is a real,
  // already-actionable result state and is left untouched.
  useEffect(() => {
    function handlePageShow(event) {
      if (!event.persisted) return;
      // The restored page's request is dead, and the reset is no click: it
      // moves no focus (header, FOCUS).
      disarmFocusHandoff();
      setPhase((prev) => {
        if (prev !== 'submitting' && prev !== 'redirecting') return prev;
        return resolvedPriceId ? 'idle' : 'unconfigured';
      });
      setMessage('');
      setSteps([]);
    }
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [resolvedPriceId, disarmFocusHandoff]);

  const beginCheckout = useCallback(async (event) => {
    // Before the date-of-birth prompt below opens: it captures this same
    // button as its opener, returns focus to it on close, and the catch then
    // swaps the button for the ageRequired panel.
    armFocusHandoff(event?.currentTarget);
    if (selfCheckout !== 'age_unknown') {
      start();
      return;
    }
    try {
      await requestDob();
    } catch {
      if (unmountedRef.current) return;
      setPhase('ageRequired');
      setMessage('Add your date of birth first, then try again.');
      return;
    }
    if (unmountedRef.current) return;
    start();
  }, [selfCheckout, start, armFocusHandoff]);

  // 'guardian_required' / 'not_student' (review S1/HIGH, S2/HIGH): render
  // DIRECTLY, ahead of (and instead of) the phase state machine below, so
  // there is no render — not even a single frame — in which a self-buy
  // button exists for either case, and no `POST /billing/checkout-session`
  // is ever attempted. No internal dismiss affordance is needed here: a
  // modal caller (UpgradeDialog) already provides its OWN persistent close
  // button outside this component (mirrors the existing `unconfigured`
  // phase's same "no button, no dismiss" precedent below); a persistent
  // page section (PremiumHub/EntitlementStatusBlock) has nothing to
  // dismiss TO — this state doesn't change without navigating away.
  if (selfCheckout === 'guardian_required') {
    return (
      <div className="checkout-flow__result checkout-flow__result--encourage" role="status">
        {/* The EXACT copy billing_checkout.py's resolve_target raises
            reactively for this same case (age < SELF_PURCHASE_MIN_AGE) —
            kept word-for-word so the proactive and reactive paths (the
            fallback for an older backend) read identically. */}
        <p className="checkout-flow__message" tabIndex={-1} {...landing}>
          A parent or guardian needs to set this up for you &mdash; everything
          you have today stays free either way.
        </p>
        <ul className="checkout-flow__steps">
          <li>Ask your parent or guardian to sign in and upgrade for you.</li>
          <li>
            Keep practicing &mdash; your learning paths and prep practice are
            always free.
          </li>
        </ul>
      </div>
    );
  }

  if (selfCheckout === 'not_student') {
    return (
      <div className="checkout-flow__result checkout-flow__result--info" role="status">
        <p className="checkout-flow__message" tabIndex={-1} {...landing}>
          Premium is something you turn on for a specific student, not for
          your own account.
        </p>
        {/* `/family` may or may not currently resolve for this account
            (it's registered only for a guardian — App.js `isGuardian`); if
            not, this safely lands on the app's existing catch-all home
            redirect (the same "always a real page, never a dead end"
            pattern already used for /family itself when a non-guardian
            follows a stale link there) rather than a broken/unreachable
            target. */}
        <Link to="/family" className="btn btn--primary btn--md">
          Go to your Family page
        </Link>
      </div>
    );
  }

  if (phase === 'unconfigured') {
    return (
      <p className="checkout-flow__note" role="status">
        Premium purchases aren&rsquo;t set up to buy just yet &mdash; check
        back soon.
      </p>
    );
  }

  if (phase === 'idle' || phase === 'submitting' || phase === 'redirecting') {
    const busy = phase !== 'idle';
    let label = buttonLabel;
    let pendingText = 'Starting checkout.';
    if (phase === 'submitting') label = 'Starting checkout…';
    if (phase === 'redirecting') {
      label = 'Redirecting…';
      pendingText = 'Redirecting to secure checkout.';
    }
    return (
      <>
        {/* ONE element through the whole request, so the focus the click put
            here stays here (header, FOCUS): busy is aria-disabled, never
            `disabled`/`loading`, and <Button> then refuses its activation. */}
        <Button
          {...landing}
          variant="primary"
          aria-disabled={busy || undefined}
          onClick={beginCheckout}
        >
          {busy && <Spinner size="sm" decorative />}
          {label}
        </Button>
        {/* Mounted before the click, so its text CHANGE is what's announced
            (see PendingStatus). */}
        <PendingStatus pending={busy}>{pendingText}</PendingStatus>
      </>
    );
  }

  if (phase === 'already') {
    const portalBusy = portal.state === 'loading';
    // One landing at a time: this panel's message, until a portal refusal
    // brings its own note below.
    const messageLanding = portal.state === 'error' ? null : landing;
    return (
      <div className="checkout-flow__result checkout-flow__result--success" role="status">
        {/* The backend's OWN message already ends "— nothing more to buy."
            (already_entitled's message) — render it verbatim, never append
            a second, duplicate sentence (review B3). */}
        <p className="checkout-flow__message" tabIndex={-1} {...messageLanding}>
          {message}
        </p>
        <NextStepsList steps={steps} />
        <div className="checkout-flow__actions">
          {/* Busy like the Upgrade button: aria-disabled, so it keeps focus
              while the portal request runs. No PendingStatus beside it: this
              panel is a role="status" region already, and a second one inside
              it would announce twice. */}
          <Button
            variant="outline"
            aria-disabled={portalBusy || undefined}
            onClick={manageBilling}
          >
            {portalBusy ? 'Opening…' : 'Manage billing'}
          </Button>
          <Button variant="ghost" onClick={dismiss}>
            Great, thanks
          </Button>
        </div>
        {portal.state === 'error' && (
          <div className="checkout-flow__note" role="status">
            <p tabIndex={-1} {...landing}>{portal.message}</p>
            {portal.steps.length > 0 && (
              <ul className="checkout-flow__steps">
                {portal.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  }

  if (phase === 'guardianReq') {
    return (
      <div className="checkout-flow__result checkout-flow__result--encourage" role="status">
        <p className="checkout-flow__message" tabIndex={-1} {...landing}>{message}</p>
        <NextStepsList steps={steps} />
        <Button variant="ghost" onClick={dismiss}>
          Got it
        </Button>
      </div>
    );
  }

  if (phase === 'inProgress') {
    return (
      <div className="checkout-flow__result checkout-flow__result--info" role="status">
        <p className="checkout-flow__message" tabIndex={-1} {...landing}>{message}</p>
        <NextStepsList steps={steps} />
        <div className="checkout-flow__actions">
          <Button variant="outline" onClick={start}>
            Try again
          </Button>
          <Button variant="ghost" onClick={dismiss}>
            Not now
          </Button>
        </div>
      </div>
    );
  }

  if (phase === 'ageRequired') {
    return (
      <div className="checkout-flow__result checkout-flow__result--encourage" role="status">
        <p className="checkout-flow__message" tabIndex={-1} {...landing}>{message}</p>
        <div className="checkout-flow__actions">
          {/* Routes back through beginCheckout, not start directly: if the
              account is STILL age_unknown (the DOB modal was dismissed
              without submitting), this re-prompts DOB proactively instead
              of firing another doomed POST — same minimization reasoning as
              the idle button above. Falls through to start() exactly like
              beginCheckout always does once selfCheckout isn't
              'age_unknown' (including the fully-reactive fallback path that
              landed here in the first place on an older backend).

              While it re-prompts, this button is the prompt's opener and stays
              on screen behind it. Dismissed again, the prompt hands focus
              straight back here: this panel has not changed, so no hand-off
              lands anywhere. The Enter that pressed the prompt's "Not now" may
              still be down, and its repeat would press this and reopen the
              prompt the student just declined. */}
          <Button variant="outline" onKeyDown={refuseHeldEnter} onClick={beginCheckout}>
            Try again
          </Button>
          <Button variant="ghost" onClick={dismiss}>
            Not now
          </Button>
        </div>
      </div>
    );
  }

  if (phase === 'stripeDown') {
    return (
      <div className="checkout-flow__result checkout-flow__result--error" role="alert">
        <p className="checkout-flow__message" tabIndex={-1} {...landing}>{message}</p>
        <div className="checkout-flow__actions">
          <Button variant="outline" onClick={start}>
            Try again
          </Button>
          <Button variant="ghost" onClick={dismiss}>
            Not now
          </Button>
        </div>
      </div>
    );
  }

  if (phase === 'invalid') {
    return (
      <div className="checkout-flow__result checkout-flow__result--info" role="status">
        <p className="checkout-flow__message" tabIndex={-1} {...landing}>{message}</p>
        <NextStepsList steps={steps} />
        <Button variant="ghost" onClick={dismiss}>
          Got it
        </Button>
      </div>
    );
  }

  // 'error' — generic fallback.
  return (
    <div className="checkout-flow__result checkout-flow__result--info" role="status">
      <p className="checkout-flow__message" tabIndex={-1} {...landing}>{message}</p>
      <div className="checkout-flow__actions">
        <Button variant="outline" onClick={start}>
          Try again
        </Button>
        <Button variant="ghost" onClick={dismiss}>
          Not now
        </Button>
      </div>
    </div>
  );
}
